import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, FileClock, FileCheck, Calendar, Plus, Users, 
  Settings, LogOut, User, Heart, DollarSign, 
  TrendingUp, Bell, ChevronRight, Calculator, FileSpreadsheet,
  Briefcase, Award, Eye, Clock, Home, AlertTriangle, Phone, CalendarClock,
  Scissors, Zap, Handshake, X, CheckCircle, Star, Shield, ChevronDown, Trash2, Gift, ShoppingBag,
  UserCog, Sun, Moon, Wrench
} from 'lucide-react';
import useBrandLogo from '../hooks/useBrandLogo';
import MobileHeader from '../components/MobileHeader';
import InstallAppButton from '../components/InstallAppButton';
import MetricasVendedores from '../components/MetricasVendedores';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Configuración de badges de roles
const ROLE_BADGES = {
  gerencia: { label: 'Gerencia', color: 'bg-purple-50 text-purple-700 border border-purple-200' },
  admin: { label: 'Admin', color: 'bg-blue-50 text-blue-700 border border-blue-200' },
  asesor: { label: 'Asesor', color: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  tienda: { label: 'Tienda', color: 'bg-orange-50 text-orange-700 border border-orange-200' },
  diseñador: { label: 'Diseñador', color: 'bg-amber-50 text-amber-700 border border-amber-200' },
  contabilidad: { label: 'Contabilidad', color: 'bg-cyan-50 text-cyan-700 border border-cyan-200' },
  aliado: { label: 'Aliado', color: 'bg-yellow-50 text-yellow-700 border border-yellow-200' }
};

// Componente de Tarjeta de Estadística
const StatCard = ({ icon: Icon, label, value, iconBg, iconColor, onClick, subtitle }) => (
  <div
    onClick={onClick}
    className={`group bg-white rounded-xl p-5 border border-warm-100 shadow-xs hover:shadow-md hover:border-warm-200 transition-all duration-300 ${onClick ? 'cursor-pointer hover:-translate-y-1' : ''}`}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className={`w-11 h-11 ${iconBg} rounded-xl flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-105`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <p className="font-serif text-3xl font-medium text-black mb-1 tracking-tight">
      {typeof value === 'number' ? value.toLocaleString('es-CO') : value}
    </p>
    <p className="text-[10px] uppercase tracking-[0.1em] text-warm-500 font-medium">
      {label}
    </p>
    {subtitle && (
      <p className="text-xs text-warm-400 mt-1">{subtitle}</p>
    )}
  </div>
);

// Componente de Botón de Acceso Rápido
const QuickAccessButton = ({ icon: Icon, label, onClick, variant = 'default' }) => {
  const variants = {
    default: 'bg-white border-warm-100 hover:border-warm-300 text-warm-700',
    primary: 'bg-black text-white hover:bg-warm-800',
    gold: 'bg-[#D4AF37] text-black hover:bg-[#C49F27]',
    success: 'bg-emerald-50 border-emerald-200 hover:border-emerald-400 text-emerald-700'
  };
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border transition-all hover:shadow-md text-left ${variants[variant]}`}
      data-testid={`quick-btn-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
};

// Componente de Módulo del Dashboard - NUEVO DISEÑO CON CONTENEDOR VISUAL
const DashboardModule = ({ 
  title, 
  subtitle, 
  icon: Icon, 
  children, 
  actionButton,
  variant = 'default', // 'fichas', 'cotizaciones', 'crm', 'aliados', 'vendedores', 'accesos'
  testId
}) => {
  // Configuración de estilos por módulo según design guidelines
  const moduleStyles = {
    fichas: {
      container: 'bg-slate-50 border-slate-200',
      header: 'text-slate-800',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      accentBorder: 'border-l-slate-500'
    },
    cotizaciones: {
      container: 'bg-sky-50/50 border-sky-100',
      header: 'text-sky-900',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      accentBorder: 'border-l-sky-500'
    },
    crm: {
      container: 'bg-emerald-50/50 border-emerald-100',
      header: 'text-emerald-900',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      accentBorder: 'border-l-emerald-500'
    },
    aliados: {
      container: 'bg-amber-50/30 border-amber-200/60',
      header: 'text-amber-900',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      accentBorder: 'border-l-amber-500'
    },
    vendedores: {
      container: 'bg-orange-50/40 border-orange-100',
      header: 'text-orange-900',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      accentBorder: 'border-l-orange-500'
    },
    accesos: {
      container: 'bg-white border-slate-200',
      header: 'text-slate-800',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-500',
      accentBorder: 'border-l-slate-400'
    },
    default: {
      container: 'bg-white border-warm-100',
      header: 'text-warm-900',
      iconBg: 'bg-warm-100',
      iconColor: 'text-warm-700',
      accentBorder: 'border-l-warm-400'
    }
  };

  const style = moduleStyles[variant] || moduleStyles.default;

  return (
    <section 
      className={`mb-8 p-6 md:p-8 rounded-xl border shadow-sm ${style.container} border-l-4 ${style.accentBorder}`}
      data-testid={testId || `module-${variant}`}
    >
      {/* Header del módulo */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-11 h-11 rounded-xl ${style.iconBg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
            <Icon className={`w-5 h-5 ${style.iconColor}`} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <h2 className={`font-serif text-xl md:text-2xl tracking-tight ${style.header}`}>{title}</h2>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5 font-light">{subtitle}</p>}
          </div>
        </div>
        {actionButton && (
          <div className="flex-shrink-0">
            {actionButton}
          </div>
        )}
      </div>
      {/* Contenido del módulo */}
      <div className="space-y-5">
        {children}
      </div>
    </section>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, canManageUsers, canCreateSheets, isGerencia } = useAuth();
  const { logoUrl } = useBrandLogo();
  
  // Estados para métricas
  const [fichasStats, setFichasStats] = useState(null);
  const [cotizacionesStats, setCotizacionesStats] = useState(null);
  const [aliadosStats, setAliadosStats] = useState(null);
  const [ventasStats, setVentasStats] = useState(null); // Métricas de ventas/cartera
  const [recordatoriosCount, setRecordatoriosCount] = useState(0);
  const [recordatoriosData, setRecordatoriosData] = useState([]); // Datos completos de recordatorios
  const [loading, setLoading] = useState(true);
  
  // Theme
  const { isDarkMode, toggleTheme } = useTheme();
  
  // Estados para modal de descarte
  const [showDescartarModal, setShowDescartarModal] = useState(false);
  const [recordatorioADescartar, setRecordatorioADescartar] = useState(null);
  const [descartando, setDescartando] = useState(false);
  const [showVerTodas, setShowVerTodas] = useState(false); // Modal para ver todas las alertas

  // Cargar todas las métricas
  const fetchAllStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Cargar estadísticas en paralelo
      const [statsRes, quotesRes, recordatoriosRes] = await Promise.all([
        axios.get(`${API_URL}/stats`, { headers }),
        axios.get(`${API_URL}/quotes/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/quotes/recordatorios`, { headers }).catch(() => ({ data: { recordatorios: [] } }))
      ]);

      setFichasStats(statsRes.data);
      setCotizacionesStats(quotesRes.data);
      const recordatorios = recordatoriosRes.data.recordatorios || [];
      setRecordatoriosCount(recordatorios.length);
      setRecordatoriosData(recordatorios); // Guardar datos completos

      // Cargar stats de aliados solo para gerencia/admin
      if (user?.role === 'gerencia' || user?.role === 'admin') {
        try {
          const aliadosRes = await axios.get(`${API_URL}/aliados/dashboard`, { headers });
          setAliadosStats(aliadosRes.data);
        } catch (e) {
          console.debug('Aliados stats not available');
        }
        
        // Cargar métricas de ventas/cartera
        try {
          const ventasRes = await axios.get(`${API_URL}/ventas/dashboard/metricas`, { headers });
          setVentasStats(ventasRes.data);
        } catch (e) {
          console.debug('Ventas stats not available');
        }
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  // Función para descartar recordatorio
  const handleDescartarRecordatorio = async () => {
    if (!recordatorioADescartar) return;
    
    setDescartando(true);
    try {
      const token = localStorage.getItem('token');
      
      // Determinar el ID de la entidad según el tipo
      const entidadId = recordatorioADescartar.quote_id || recordatorioADescartar.sheet_id || recordatorioADescartar.cliente_id;
      
      await axios.post(
        `${API_URL}/recordatorios/descartar`,
        {
          tipo: recordatorioADescartar.tipo,
          entidad_id: entidadId,
          motivo: 'Descartado manualmente desde dashboard'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Actualizar la lista local de recordatorios
      const nuevosRecordatorios = recordatoriosData.filter(r => {
        const rEntidadId = r.quote_id || r.sheet_id || r.cliente_id;
        return !(r.tipo === recordatorioADescartar.tipo && rEntidadId === entidadId);
      });
      
      setRecordatoriosData(nuevosRecordatorios);
      setRecordatoriosCount(nuevosRecordatorios.length);
      
      toast.success('Recordatorio descartado correctamente');
      setShowDescartarModal(false);
      setRecordatorioADescartar(null);
    } catch (error) {
      console.error('Error al descartar:', error);
      toast.error(error.response?.data?.detail || 'Error al descartar el recordatorio');
    } finally {
      setDescartando(false);
    }
  };

  // Función para abrir modal de descarte
  const abrirModalDescartar = (recordatorio, e) => {
    e.stopPropagation(); // Evitar navegación
    setRecordatorioADescartar(recordatorio);
    setShowDescartarModal(true);
  };

  useEffect(() => {
    fetchAllStats();
  }, [fetchAllStats]);

  const handleLogout = () => {
    logout();
  };

  const userRoleBadge = ROLE_BADGES[user?.role] || ROLE_BADGES.asesor;

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <MobileHeader />
        <div className="hidden md:block bg-black h-18" />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-warm-100 animate-pulse">
                <div className="w-11 h-11 rounded-xl bg-warm-100 mb-3" />
                <div className="h-8 bg-warm-100 rounded-lg mb-2 w-16" />
                <div className="h-3 bg-warm-100 rounded w-20" />
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* ============================================ */}
      {/* MODAL DE CONFIRMACIÓN PARA DESCARTAR */}
      {/* ============================================ */}
      {showDescartarModal && recordatorioADescartar && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowDescartarModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-serif text-xl text-slate-900">¿Descartar recordatorio?</h3>
                <p className="text-sm text-slate-500">Esta acción no elimina ningún dato</p>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="font-medium text-slate-900 mb-1">
                {recordatorioADescartar.cliente || recordatorioADescartar.numero || 'Recordatorio'}
              </p>
              <p className="text-sm text-slate-600">
                {recordatorioADescartar.mensaje || recordatorioADescartar.tipo}
              </p>
            </div>
            
            <p className="text-sm text-slate-600 mb-6">
              El recordatorio dejará de aparecer en el panel de <strong>Atención Requerida</strong>. 
              No se eliminará la ficha, cotización, ni ningún dato relacionado.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDescartarModal(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleDescartarRecordatorio}
                disabled={descartando}
                className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {descartando ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Descartando...
                  </>
                ) : (
                  'Sí, descartar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL VER TODAS LAS ALERTAS */}
      {/* ============================================ */}
      {showVerTodas && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowVerTodas(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header del modal */}
            <div className="p-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6" />
                <div>
                  <h2 className="font-serif text-xl">Atención Requerida</h2>
                  <p className="text-sm text-white/80">{recordatoriosCount} {recordatoriosCount === 1 ? 'recordatorio pendiente' : 'recordatorios pendientes'}</p>
                </div>
              </div>
              <button onClick={() => setShowVerTodas(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Lista de recordatorios */}
            <div className="flex-1 overflow-y-auto p-5">
              {recordatoriosData.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                  <h3 className="font-serif text-xl text-slate-900 mb-2">¡Todo al día!</h3>
                  <p className="text-slate-500">No hay recordatorios pendientes por ahora.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recordatoriosData.map((item, idx) => (
                    <div
                      key={`${item.tipo}-${item.quote_id || item.sheet_id || idx}`}
                      className={`p-4 rounded-xl border-l-4 ${
                        item.prioridad === 'alta' ? 'bg-red-50 border-red-500' :
                        item.prioridad === 'media' ? 'bg-amber-50 border-amber-500' :
                        'bg-slate-50 border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-slate-900">{item.numero || item.cliente}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              item.prioridad === 'alta' ? 'bg-red-100 text-red-700' :
                              item.prioridad === 'media' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {item.tipo === 'sin_respuesta' ? 'Sin seguimiento' :
                               item.tipo === 'proxima_vencer' ? 'Por vencer' :
                               item.tipo === 'venta_sin_ficha' ? 'Sin ficha' :
                               item.tipo === 'ficha_sin_medidas' ? 'Sin medidas' :
                               item.tipo === 'ficha_sin_prueba' ? 'Sin prueba' :
                               item.tipo}
                            </span>
                          </div>
                          <p className="font-serif text-lg text-slate-900">{item.cliente}</p>
                          <p className="text-sm text-slate-600">{item.mensaje}</p>
                          {item.valor && (
                            <p className="text-sm font-medium text-amber-600 mt-1">
                              ${item.valor?.toLocaleString('es-CO')}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          {item.telefono && (
                            <a
                              href={`tel:${item.telefono}`}
                              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 transition-colors"
                            >
                              <Phone className="w-3.5 h-3.5" />
                              Llamar
                            </a>
                          )}
                          <button
                            onClick={() => {
                              if (item.quote_id) navigate(`/quotes/${item.quote_id}`);
                              else if (item.sheet_id) navigate(`/sheet/${item.sheet_id}`);
                              setShowVerTodas(false);
                            }}
                            className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-medium hover:bg-slate-800 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Ver
                          </button>
                          <button
                            onClick={(e) => { setShowVerTodas(false); abrirModalDescartar(item, e); }}
                            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors"
                            title="Descartar recordatorio"
                          >
                            <X className="w-3.5 h-3.5" />
                            Descartar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Los recordatorios descartados no eliminan ningún dato
              </p>
              <button
                onClick={() => setShowVerTodas(false)}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Desktop Header - Fijo con backdrop blur y Safe Area */}
      <header className="bg-black/95 backdrop-blur-xl fixed top-0 left-0 w-full z-50 hidden md:block border-b border-zinc-800 shadow-lg safe-area-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 py-4">
            {/* Logo + Brand */}
            <div className="flex items-center gap-5">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Logo" 
                  className="h-11 w-auto object-contain brightness-0 invert"
                  data-testid="brand-logo"
                />
              ) : (
                <div className="w-11 h-11 bg-white/10 animate-pulse rounded-lg" />
              )}
              <div className="hidden sm:block">
                <h1 className="font-serif text-xl text-white tracking-wide leading-tight">
                  ATELIER <span className="text-white/40">—</span> JPM
                </h1>
                <p className="text-[9px] uppercase tracking-[0.15em] text-white/50 mt-0.5">
                  Juan Pablo Murillo Tailoring System
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-1">
              {/* Toggle de Tema - Visible siempre */}
              <button
                onClick={toggleTheme}
                className="relative flex items-center justify-center w-10 h-10 text-white/70 hover:text-white hover:bg-white/10 transition-all duration-300 rounded-lg"
                title={isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                data-testid="theme-toggle"
              >
                <div className="relative w-5 h-5">
                  <Sun 
                    className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                      isDarkMode 
                        ? 'opacity-0 rotate-90 scale-0' 
                        : 'opacity-100 rotate-0 scale-100 text-amber-400'
                    }`}
                  />
                  <Moon 
                    className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                      isDarkMode 
                        ? 'opacity-100 rotate-0 scale-100 text-amber-400' 
                        : 'opacity-0 -rotate-90 scale-0'
                    }`}
                  />
                </div>
              </button>

              {/* Botón Usuarios visible - Solo Gerencia con permisos */}
              {canManageUsers() && (
                <button
                  onClick={() => navigate('/admin/users')}
                  className="flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200 rounded-lg"
                  data-testid="header-usuarios-btn"
                >
                  <UserCog className="w-4 h-4" />
                  <span className="hidden md:inline">Usuarios</span>
                </button>
              )}

              {/* Dropdown de Configuración - Solo Gerencia */}
              {isGerencia() && (
                <div className="relative group">
                  <button
                    className="flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200 rounded-lg"
                    data-testid="config-dropdown"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden md:inline">Config</span>
                    <ChevronDown className="w-3 h-3 opacity-50" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 min-w-[200px]">
                      {canManageUsers() && (
                        <button
                          onClick={() => navigate('/admin/users')}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors"
                        >
                          <Users className="w-4 h-4" />
                          Usuarios
                        </button>
                      )}
                      <button
                        onClick={() => navigate('/admin/brand')}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        Marca
                      </button>
                      <button
                        onClick={() => navigate('/admin/condiciones-venta')}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors"
                      >
                        <Shield className="w-4 h-4" />
                        Términos
                      </button>
                      <button
                        onClick={() => navigate('/admin/exchange-rates')}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors"
                      >
                        <DollarSign className="w-4 h-4" />
                        Tasas de Cambio
                      </button>
                      <div className="border-t border-zinc-700 my-1" />
                      <button
                        onClick={() => navigate('/export/modules')}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-[#D4AF37] hover:text-[#E5C048] hover:bg-[#D4AF37]/10 transition-colors"
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                        Exportar
                      </button>
                      <button
                        onClick={() => navigate('/admin/mantenimiento')}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors"
                        data-testid="config-mantenimiento-link"
                      >
                        <Wrench className="w-4 h-4" />
                        Mantenimiento
                      </button>
                      <button
                        onClick={() => navigate('/registros-eliminados')}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminados
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="hidden md:block w-px h-8 bg-white/15 mx-3" />

              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-3 px-3 py-2 hover:bg-white/10 transition-all duration-200 rounded-lg"
              >
                <div className="w-9 h-9 rounded-xl overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center">
                  {user?.profile_photo ? (
                    <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-4 h-4 text-white/70" />
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm text-white leading-tight font-medium">{user?.username}</p>
                  <p className="text-[9px] uppercase tracking-wider text-white/50">{userRoleBadge.label}</p>
                </div>
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2.5 text-[10px] uppercase tracking-wider text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200 rounded-lg ml-1"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ============================================ */}
      {/* CINTA DE NAVEGACIÓN PRINCIPAL - Completa */}
      {/* Orden: Inicio · Cotizaciones · Ventas · Clientes · CRM · Vendedores · Aliados · Fichas */}
      {/* Fichas al final porque es el último paso del proceso comercial */}
      {/* + Botones de creación: Nueva cotización, Nueva ficha */}
      {/* ============================================ */}
      <div className="sticky top-0 md:top-[72px] z-40 bg-zinc-900 border-b border-zinc-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Mobile: Scroll horizontal */}
          <div className="md:hidden">
            <div className="flex items-center gap-1.5 py-2 overflow-x-auto scrollbar-hide -mx-3 px-3">
              {/* Navegación principal - Optimizada para móvil */}
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-1.5 px-3 py-2.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg text-[11px] font-medium whitespace-nowrap min-h-[44px]"
              >
                <Home className="w-4 h-4" />
                Inicio
              </button>
              <button
                onClick={() => navigate('/quotes')}
                className="flex items-center gap-1.5 px-3 py-2.5 text-zinc-400 hover:text-white active:bg-zinc-700 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors min-h-[44px]"
              >
                <Calculator className="w-4 h-4" />
                Cotiz.
              </button>
              <button
                onClick={() => navigate('/ventas')}
                className="flex items-center gap-1.5 px-3 py-2.5 text-zinc-400 hover:text-white active:bg-zinc-700 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors min-h-[44px]"
              >
                <DollarSign className="w-4 h-4" />
                Ventas
              </button>
              <button
                onClick={() => navigate('/clientes')}
                className="flex items-center gap-1.5 px-3 py-2.5 text-zinc-400 hover:text-white active:bg-zinc-700 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors min-h-[44px]"
              >
                <Users className="w-4 h-4" />
                Clientes
              </button>
              <button
                onClick={() => navigate('/crm')}
                className="flex items-center gap-1.5 px-3 py-2.5 text-zinc-400 hover:text-white active:bg-zinc-700 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors min-h-[44px]"
              >
                <Briefcase className="w-4 h-4" />
                CRM
              </button>
              {(user?.role === 'gerencia' || user?.role === 'admin') && (
                <>
                  <button
                    onClick={() => navigate('/vendedores')}
                    className="flex items-center gap-1.5 px-3 py-2.5 text-zinc-400 hover:text-white active:bg-zinc-700 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors min-h-[44px]"
                  >
                    <Award className="w-4 h-4" />
                    Vend.
                  </button>
                  <button
                    onClick={() => navigate('/aliados')}
                    className="flex items-center gap-1.5 px-3 py-2.5 text-zinc-400 hover:text-white active:bg-zinc-700 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors min-h-[44px]"
                  >
                    <Heart className="w-4 h-4" />
                    Aliados
                  </button>
                </>
              )}
              <button
                onClick={() => navigate('/fichas')}
                className="flex items-center gap-1.5 px-3 py-2.5 text-zinc-400 hover:text-white active:bg-zinc-700 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors min-h-[44px]"
              >
                <Scissors className="w-4 h-4" />
                Fichas
              </button>
              {/* Separador visual */}
              <div className="w-px h-8 bg-zinc-700 mx-1 flex-shrink-0" />
              {/* Botones de creación */}
              <button
                onClick={() => navigate('/quotes/new')}
                className="flex items-center gap-1.5 px-3 py-2.5 bg-sky-600 active:bg-sky-700 text-white rounded-lg text-[11px] font-medium whitespace-nowrap min-h-[44px]"
              >
                <Plus className="w-4 h-4" />
                + Cotiz.
              </button>
              <button
                onClick={() => navigate('/sheet/new')}
                className="flex items-center gap-1.5 px-3 py-2.5 bg-emerald-600 active:bg-emerald-700 text-white rounded-lg text-[11px] font-medium whitespace-nowrap min-h-[44px]"
              >
                <Plus className="w-4 h-4" />
                + Ficha
              </button>
            </div>
          </div>
          
          {/* Desktop: Fila única con navegación y creación */}
          <div className="hidden md:flex items-center justify-between py-2">
            {/* Grupo izquierdo: Navegación */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 text-[#D4AF37] rounded-lg text-[11px] uppercase tracking-wider font-medium"
                data-testid="nav-inicio"
              >
                <Home className="w-4 h-4" />
                Inicio
              </button>
              <button
                onClick={() => navigate('/quotes')}
                className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                data-testid="nav-cotizaciones"
              >
                <Calculator className="w-4 h-4" />
                Cotizaciones
              </button>
              <button
                onClick={() => navigate('/ventas')}
                className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                data-testid="nav-ventas"
              >
                <DollarSign className="w-4 h-4" />
                Ventas
              </button>
              <button
                onClick={() => navigate('/clientes')}
                className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                data-testid="nav-clientes"
              >
                <Users className="w-4 h-4" />
                Clientes
              </button>
              <button
                onClick={() => navigate('/crm')}
                className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                data-testid="nav-crm"
              >
                <Briefcase className="w-4 h-4" />
                CRM
              </button>
              {(user?.role === 'gerencia' || user?.role === 'admin') && (
                <>
                  <button
                    onClick={() => navigate('/vendedores')}
                    className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                    data-testid="nav-vendedores"
                  >
                    <Award className="w-4 h-4" />
                    Vendedores
                  </button>
                  <button
                    onClick={() => navigate('/aliados')}
                    className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                    data-testid="nav-aliados"
                  >
                    <Heart className="w-4 h-4" />
                    Aliados
                  </button>
                </>
              )}
              {['gerencia', 'admin', 'asesor', 'vendedor', 'asesor_diseñador'].includes(user?.role) && (
                <button
                  onClick={() => navigate('/admin/bodas')}
                  className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                  data-testid="nav-bodas"
                >
                  <Gift className="w-4 h-4" />
                  Bodas
                </button>
              )}
              {user?.role === 'gerencia' && (
                <button
                  onClick={() => navigate('/admin/tienda')}
                  className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                  data-testid="nav-tienda-online"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Tienda online
                </button>
              )}
              <button
                onClick={() => navigate('/fichas')}
                className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors"
                data-testid="nav-fichas"
              >
                <Scissors className="w-4 h-4" />
                Fichas
              </button>
            </div>
            
            {/* Grupo derecho: Botones de creación */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/quotes/new')}
                className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors shadow-sm"
                data-testid="btn-nueva-cotizacion"
              >
                <Plus className="w-4 h-4" />
                Nueva cotización
              </button>
              <button
                onClick={() => navigate('/sheet/new')}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[11px] uppercase tracking-wider font-medium transition-colors shadow-sm"
                data-testid="btn-nueva-ficha"
              >
                <Plus className="w-4 h-4" />
                Nueva ficha
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 md:mt-[calc(72px+env(safe-area-inset-top,0px))]">
        {/* Mobile Welcome */}
        <div className="md:hidden mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center overflow-hidden shadow-sm">
              {user?.profile_photo ? (
                <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-lg font-medium">{user?.username?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-warm-500">Bienvenido,</p>
              <p className="font-serif text-lg text-black truncate">{user?.username}</p>
            </div>
            <span className={`px-3 py-1.5 text-[9px] uppercase tracking-wider font-medium rounded-full ${userRoleBadge.color}`}>
              {userRoleBadge.label}
            </span>
          </div>
        </div>

        {/* ============================================ */}
        {/* BOTÓN INSTALAR APP (PWA) */}
        {/* ============================================ */}
        <InstallAppButton variant="card" />

        {/* ============================================ */}
        {/* SEGUIMIENTOS PENDIENTES - CON ACCIONES */}
        {/* ============================================ */}
        {recordatoriosCount > 0 && (
          <section className="mb-6" data-testid="seguimientos-pendientes-section">
            <div className="bg-white border border-amber-200 rounded-xl overflow-hidden shadow-sm">
              {/* Header con título y botones principales */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">
                      {recordatoriosCount} {recordatoriosCount !== 1 ? 'seguimientos pendientes' : 'seguimiento pendiente'}
                    </p>
                    <p className="text-xs text-amber-600">Atención prioritaria</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => navigate('/quotes/new')}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-black hover:bg-zinc-800 text-white text-sm font-medium rounded-lg transition-colors"
                    data-testid="quick-new-quote"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Nueva cotización</span>
                    <span className="sm:hidden">Nueva</span>
                  </button>
                  <button
                    onClick={() => setShowVerTodas(true)}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Ver pendientes
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Lista de recordatorios con acciones */}
              <div className="divide-y divide-amber-100">
                {recordatoriosData.slice(0, 5).map((item, index) => (
                  <div
                    key={`${item.tipo}-${item.quote_id || item.sheet_id || index}`}
                    className={`p-4 hover:bg-amber-50/50 transition-colors cursor-pointer ${
                      item.prioridad === 'alta' ? 'bg-red-50/30' : ''
                    }`}
                    onClick={() => {
                      if (item.quote_id) navigate(`/quotes/${item.quote_id}`);
                      else if (item.sheet_id) navigate(`/sheet/${item.sheet_id}`);
                    }}
                    data-testid={`recordatorio-item-${index}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      {/* Información del recordatorio */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {item.prioridad === 'alta' ? (
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        ) : item.prioridad === 'media' ? (
                          <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Bell className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-zinc-900">
                              {item.numero || item.cotizacion_numero}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              item.prioridad === 'alta' ? 'bg-red-100 text-red-700' : 
                              item.prioridad === 'media' ? 'bg-amber-100 text-amber-700' : 
                              'bg-zinc-100 text-zinc-600'
                            }`}>
                              {item.tipo === 'sin_respuesta' ? 'Sin seguimiento' :
                               item.tipo === 'proxima_vencer' ? 'Por vencer' :
                               item.tipo === 'venta_sin_ficha' ? 'Sin ficha' :
                               item.tipo === 'seguimiento_programado' ? 'Programado' :
                               item.tipo}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-700 mt-0.5">{item.cliente}</p>
                          <p className="text-xs text-zinc-500 mt-1">{item.mensaje}</p>
                        </div>
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {/* Teléfono de contacto rápido */}
                        {item.telefono && (
                          <a
                            href={`https://wa.me/57${item.telefono.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 transition-colors"
                            title="Contactar por WhatsApp"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Llamar</span>
                          </a>
                        )}
                        <button
                          onClick={(e) => abrirModalDescartar(item, e)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors"
                          title="Descartar este recordatorio"
                          data-testid={`descartar-recordatorio-${index}`}
                        >
                          <X className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Descartar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {recordatoriosCount > 5 && (
                  <button 
                    onClick={() => setShowVerTodas(true)}
                    className="w-full text-center text-sm text-amber-700 py-3 hover:text-amber-900 hover:bg-amber-50 transition-colors"
                  >
                    ... y {recordatoriosCount - 5} {recordatoriosCount - 5 !== 1 ? 'pendientes más' : 'pendiente más'} (Ver todas)
                  </button>
                )}
              </div>
              
              {/* Botón ver todas (mobile) */}
              <div className="sm:hidden p-4 pt-0">
                <button
                  onClick={() => setShowVerTodas(true)}
                  className="w-full flex items-center justify-center gap-2 bg-amber-500 text-white hover:bg-amber-600 px-4 py-3 rounded-xl text-sm font-bold transition-colors"
                >
                  Ver todas las cotizaciones pendientes
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ============================================ */}
        {/* MÓDULO: COTIZACIONES */}
        {/* ============================================ */}
        <DashboardModule
          title="Cotizaciones"
          subtitle="Propuestas y ventas"
          icon={Calculator}
          variant="cotizaciones"
          testId="module-cotizaciones"
          actionButton={
            <button
              onClick={() => navigate('/quotes/new')}
              className="inline-flex items-center gap-2 bg-sky-600 text-white hover:bg-sky-700 h-10 px-5 text-[11px] uppercase tracking-wider font-medium transition-all duration-200 rounded-lg shadow-sm hover:shadow-md"
              data-testid="new-quote-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nueva Cotización</span>
            </button>
          }
        >
          {/* Métricas de Cotizaciones */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Calculator}
              label="Total Cotizaciones"
              value={cotizacionesStats?.total || 0}
              iconBg="bg-sky-100"
              iconColor="text-sky-600"
              onClick={() => navigate('/quotes')}
            />
            <StatCard
              icon={Clock}
              label="Pendientes"
              value={cotizacionesStats?.pendientes || 0}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              onClick={() => navigate('/quotes?estado=pendiente')}
            />
            <StatCard
              icon={TrendingUp}
              label="Ventas"
              value={cotizacionesStats?.ventas || 0}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              onClick={() => navigate('/quotes?estado=venta')}
            />
            <StatCard
              icon={DollarSign}
              label="Valor Total"
              value={`$${((cotizacionesStats?.valor_total || 0) / 1000000).toFixed(1)}M`}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              subtitle="Vendido (ventas vigentes)"
            />
          </div>

          {/* Botones de Acceso Rápido */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
            <QuickAccessButton
              icon={Eye}
              label="Ver Cotizaciones"
              onClick={() => navigate('/quotes')}
            />
            <QuickAccessButton
              icon={Plus}
              label="Nueva Cotización"
              onClick={() => navigate('/quotes/new')}
              variant="primary"
            />
            <QuickAccessButton
              icon={TrendingUp}
              label="Ventas"
              onClick={() => navigate('/quotes?estado=venta')}
              variant="success"
            />
            <QuickAccessButton
              icon={Clock}
              label="Pendientes"
              onClick={() => navigate('/quotes?estado=pendiente')}
            />
          </div>
        </DashboardModule>

        {/* ============================================ */}
        {/* MÓDULO: CRM / GESTIÓN DE PROSPECTOS */}
        {/* ============================================ */}
        <DashboardModule
          title="CRM / Gestión de Prospectos"
          subtitle="Control de clientes potenciales y seguimiento"
          icon={Users}
          variant="crm"
          testId="module-crm"
          actionButton={
            <button
              onClick={() => navigate('/crm')}
              className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 h-10 px-5 text-[11px] uppercase tracking-wider font-medium transition-all duration-200 rounded-lg shadow-sm hover:shadow-md"
              data-testid="ver-crm-btn"
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Ver CRM</span>
            </button>
          }
        >
          {/* Accesos Rápidos del CRM */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
            <QuickAccessButton
              icon={Users}
              label="Ver Prospectos"
              onClick={() => navigate('/crm')}
            />
            <QuickAccessButton
              icon={Plus}
              label="Nuevo Prospecto"
              onClick={() => navigate('/crm?nuevo=true')}
              variant="primary"
            />
            <QuickAccessButton
              icon={FileSpreadsheet}
              label="Importar Excel"
              onClick={() => navigate('/crm?importar=true')}
              variant="success"
            />
          </div>
        </DashboardModule>

        {/* ============================================ */}
        {/* MÓDULO: VENTAS Y CARTERA (Solo Gerencia/Admin) */}
        {/* ============================================ */}
        {(user?.role === 'gerencia' || user?.role === 'admin') && ventasStats && (
          <DashboardModule
            title="Ventas y Cartera"
            subtitle="Estado de cuenta y recaudo"
            icon={DollarSign}
            variant="cotizaciones"
            testId="module-ventas-cartera"
            actionButton={
              <button
                onClick={() => navigate('/ventas')}
                className="inline-flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 h-10 px-5 text-[11px] uppercase tracking-wider font-medium transition-all duration-200 rounded-lg shadow-sm hover:shadow-md"
                data-testid="ver-ventas-btn"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Ver Ventas</span>
              </button>
            }
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={FileCheck}
                label="Ventas Confirmadas"
                value={ventasStats.total_ventas || 0}
                iconBg="bg-emerald-50"
                iconColor="text-emerald-600"
                onClick={() => navigate('/ventas')}
              />
              <StatCard
                icon={DollarSign}
                label="Dinero Recaudado"
                value={`$${((ventasStats.dinero_recaudado || 0) / 1000000).toFixed(1)}M`}
                iconBg="bg-green-50"
                iconColor="text-green-600"
                onClick={() => navigate('/ventas')}
                subtitle={`${ventasStats.porcentaje_recaudo || 0}% del total`}
              />
              <StatCard
                icon={TrendingUp}
                label="Cartera Pendiente"
                value={`$${((ventasStats.cartera_pendiente || 0) / 1000000).toFixed(1)}M`}
                iconBg="bg-blue-50"
                iconColor="text-blue-600"
                onClick={() => navigate('/ventas')}
              />
              <StatCard
                icon={AlertTriangle}
                label="Ventas en mora"
                value={ventasStats.cuotas_vencidas || 0}
                subtitle={`${ventasStats.cuotas_vencidas_total || 0} cuotas vencidas`}
                iconBg={ventasStats.cuotas_vencidas > 0 ? "bg-red-100" : "bg-slate-50"}
                iconColor={ventasStats.cuotas_vencidas > 0 ? "text-red-600" : "text-slate-400"}
                onClick={() => navigate('/ventas?vencidas=true')}
              />
            </div>

            {/* Botones de Acceso Rápido */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
              <QuickAccessButton
                icon={FileText}
                label="Listado de Ventas"
                onClick={() => navigate('/ventas')}
              />
              <QuickAccessButton
                icon={Calculator}
                label="Crear Cotización"
                onClick={() => navigate('/quotes/new')}
              />
              <QuickAccessButton
                icon={Star}
                label="Puntos JPM"
                onClick={() => navigate('/puntos-jpm')}
                variant="gold"
              />
              {ventasStats.cuotas_vencidas > 0 && (
                <QuickAccessButton
                  icon={AlertTriangle}
                  label={`${ventasStats.cuotas_vencidas} en mora`}
                  onClick={() => navigate('/ventas?vencidas=true')}
                  variant="primary"
                />
              )}
            </div>
          </DashboardModule>
        )}

        {/* ============================================ */}
        {/* MÓDULO: ALIADOS (Solo Gerencia/Admin) */}
        {/* ============================================ */}
        {(user?.role === 'gerencia' || user?.role === 'admin') && aliadosStats && (
          <DashboardModule
            title="Módulo de Aliados"
            subtitle="Gestión de aliados, referidos y comisiones"
            icon={Handshake}
            variant="aliados"
            testId="module-aliados"
            actionButton={
              <button
                onClick={() => navigate('/aliados')}
                className="inline-flex items-center gap-2 bg-amber-500 text-white hover:bg-amber-600 h-10 px-5 text-[11px] uppercase tracking-wider font-medium transition-all duration-200 rounded-lg shadow-sm hover:shadow-md"
                data-testid="ver-aliados-btn"
              >
                <Briefcase className="w-4 h-4" />
                <span className="hidden sm:inline">Ver Todo</span>
              </button>
            }
          >
            {/* Métricas de Aliados */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Aliados Activos"
                value={aliadosStats.aliados?.activos || 0}
                iconBg="bg-amber-100"
                iconColor="text-amber-600"
                onClick={() => navigate('/aliados')}
              />
              <StatCard
                icon={TrendingUp}
                label="Total Referidos"
                value={aliadosStats.referidos?.total || 0}
                iconBg="bg-green-50"
                iconColor="text-green-600"
                onClick={() => navigate('/referidos')}
              />
              <StatCard
                icon={DollarSign}
                label="Comisiones Pend."
                value={`$${((aliadosStats.comisiones?.pendientes || 0) / 1000).toFixed(0)}K`}
                iconBg="bg-amber-50"
                iconColor="text-amber-600"
                onClick={() => navigate('/referidos?estado=pendiente')}
              />
              <StatCard
                icon={FileSpreadsheet}
                label="Cuentas Cobro"
                value={aliadosStats.cuentas_cobro?.vigentes ?? aliadosStats.cuentas_cobro?.total ?? 0}
                iconBg="bg-purple-50"
                iconColor="text-purple-600"
                onClick={() => navigate('/cuentas-cobro')}
              />
            </div>

            {/* Botones de Acceso Rápido */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
              <QuickAccessButton
                icon={Users}
                label="Ver Aliados"
                onClick={() => navigate('/aliados')}
              />
              <QuickAccessButton
                icon={Heart}
                label="Referidos Global"
                onClick={() => navigate('/referidos')}
              />
              <QuickAccessButton
                icon={FileSpreadsheet}
                label="Cuentas de Cobro"
                onClick={() => navigate('/cuentas-cobro')}
                variant="success"
              />
              <QuickAccessButton
                icon={FileText}
                label="Descargas"
                onClick={() => navigate('/descargas')}
              />
            </div>
          </DashboardModule>
        )}

        {/* ============================================ */}
        {/* MÓDULO: FICHAS DE CLIENTES (Último paso del proceso comercial) */}
        {/* ============================================ */}
        <DashboardModule
          title="Fichas de Clientes"
          subtitle="Gestión de medidas y pedidos"
          icon={Scissors}
          variant="fichas"
          testId="module-fichas"
          actionButton={
            canCreateSheets() && (
              <button
                onClick={() => navigate('/sheet/new')}
                className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 h-10 px-5 text-[11px] uppercase tracking-wider font-medium transition-all duration-200 rounded-lg shadow-sm hover:shadow-md"
                data-testid="new-sheet-btn"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Nueva Ficha</span>
              </button>
            )
          }
        >
          {/* Métricas de Fichas */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={FileText}
              label="Total Fichas"
              value={fichasStats?.total_sheets || 0}
              iconBg="bg-slate-100"
              iconColor="text-slate-600"
              onClick={() => navigate('/dashboard/fichas')}
            />
            <StatCard
              icon={FileClock}
              label="Borradores"
              value={fichasStats?.borradores || 0}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              onClick={() => navigate('/dashboard/fichas?estado=borrador')}
            />
            <StatCard
              icon={FileCheck}
              label="Finalizadas"
              value={fichasStats?.finalizadas || 0}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              onClick={() => navigate('/dashboard/fichas?estado=finalizada')}
            />
            <StatCard
              icon={Calendar}
              label="Próximos Eventos"
              value={fichasStats?.proximos_eventos ?? fichasStats?.upcoming_events?.length ?? 0}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
              onClick={() => navigate('/dashboard/fichas?proximos=true')}
            />
          </div>

          {/* Botones de Acceso Rápido */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
            <QuickAccessButton
              icon={Eye}
              label="Ver Fichas"
              onClick={() => navigate('/dashboard/fichas')}
            />
            <QuickAccessButton
              icon={Plus}
              label="Nueva Ficha"
              onClick={() => navigate('/sheet/new')}
              variant="primary"
            />
            <QuickAccessButton
              icon={Users}
              label="Clientes"
              onClick={() => navigate('/clientes')}
            />
            {(user?.role === 'gerencia' || user?.role === 'admin') && (
              <QuickAccessButton
                icon={Award}
                label="Vendedores"
                onClick={() => navigate('/vendedores')}
              />
            )}
          </div>
        </DashboardModule>

        {/* ============================================ */}
        {/* RANKING DE VENDEDORES (Solo Gerencia/Admin) */}
        {/* Solo muestra la tabla de ranking detallado, las métricas completas están en /vendedores */}
        {/* ============================================ */}
        {(user?.role === 'gerencia' || user?.role === 'admin') && (
          <section 
            className="mb-8 rounded-xl border border-zinc-800 shadow-lg overflow-hidden border-l-4 border-l-zinc-600"
            data-testid="module-ranking"
          >
            <MetricasVendedores userRole={user?.role} soloRanking={true} />
          </section>
        )}

        {/* ============================================ */}
        {/* ACCESOS RÁPIDOS GENERALES */}
        {/* ============================================ */}
        <section 
          className="p-6 md:p-8 rounded-xl border border-slate-200 bg-white shadow-sm border-l-4 border-l-slate-400"
          data-testid="module-accesos-rapidos"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center shadow-sm">
              <Zap className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif text-xl md:text-2xl tracking-tight text-slate-800">Accesos Rápidos</h3>
              <p className="text-sm text-slate-500 mt-0.5 font-light">Navegación directa a módulos</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <button
              onClick={() => navigate('/quotes')}
              className="flex flex-col items-center gap-2 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 hover:shadow-md transition-all"
              data-testid="quick-access-cotizaciones"
            >
              <Calculator className="w-6 h-6 text-sky-600" />
              <span className="text-xs font-medium text-slate-700">Cotizaciones</span>
            </button>
            <button
              onClick={() => navigate('/dashboard/fichas')}
              className="flex flex-col items-center gap-2 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all"
              data-testid="quick-access-fichas"
            >
              <FileText className="w-6 h-6 text-slate-600" />
              <span className="text-xs font-medium text-slate-700">Fichas</span>
            </button>
            <button
              onClick={() => navigate('/clientes')}
              className="flex flex-col items-center gap-2 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 hover:shadow-md transition-all"
              data-testid="quick-access-clientes"
            >
              <Users className="w-6 h-6 text-emerald-600" />
              <span className="text-xs font-medium text-slate-700">Clientes</span>
            </button>
            <button
              onClick={() => navigate('/crm')}
              className="flex flex-col items-center gap-2 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 hover:shadow-md transition-all"
              data-testid="quick-access-crm"
            >
              <Users className="w-6 h-6 text-emerald-600" />
              <span className="text-xs font-medium text-slate-700">CRM</span>
            </button>
            {(user?.role === 'gerencia' || user?.role === 'admin') && (
              <>
                <button
                  onClick={() => navigate('/aliados')}
                  className="flex flex-col items-center gap-2 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 hover:shadow-md transition-all"
                  data-testid="quick-access-aliados"
                >
                  <Heart className="w-6 h-6 text-amber-500" />
                  <span className="text-xs font-medium text-slate-700">Aliados</span>
                </button>
                <button
                  onClick={() => navigate('/vendedores')}
                  className="flex flex-col items-center gap-2 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-orange-300 hover:bg-orange-50/50 hover:shadow-md transition-all"
                  data-testid="quick-access-vendedores"
                >
                  <Award className="w-6 h-6 text-orange-600" />
                  <span className="text-xs font-medium text-slate-700">Vendedores</span>
                </button>
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};
